import { ArrowRight, ShoppingBag, Search, Menu as MenuIcon } from "lucide-react";
import SafeImage from "@/components/SafeImage";

/**
 * ExampleCard — Iter 57 rewrite.
 *
 * Renders a realistic mini website preview for one of the three SRC-based
 * templates the app actually generates. Each variant matches the visual
 * language of the corresponding /template-{ecommerce,restaurant,service}
 * app so visitors get a true feel for what they'll receive.
 *
 * The card is intentionally taller (~ 560px) than a normal marketing card so
 * we can show:
 *   1. A real nav bar (logo + 3–4 links + icons)
 *   2. A cropped hero (image + eyebrow + headline + subhead + CTA buttons)
 *   3. A peek of the section immediately below the hero (features, menu
 *      dishes, or a product grid)
 *
 * The DOM is NOT scaled with transform-origin; instead every element is sized
 * naturally for the ~380px card width. This keeps the type sharp and legible.
 */
export default function ExampleCard({ ex }) {
  if (ex.variant === "ecommerce") return <EcommercePreview ex={ex} />;
  if (ex.variant === "restaurant") return <RestaurantPreview ex={ex} />;
  return <ServicePreview ex={ex} />;
}

/* -------------------------------------------------------------------------- */
/* 1. Service template — Cornerstone Realty                                    */
/* -------------------------------------------------------------------------- */

function ServicePreview({ ex }) {
  return (
    <article
      className="shrink-0 w-[340px] sm:w-[380px] rounded-2xl overflow-hidden bg-[#faf7f0] shadow-xl ring-1 ring-black/5"
      data-testid={`example-card-${ex.slug}`}
      style={{ height: 560 }}
    >
      {/* Nav */}
      <div className="h-11 flex items-center justify-between px-4 border-b border-[#e6ded1] bg-[#faf7f0]">
        <div className="font-display text-[15px] font-black text-[#2c2a29] tracking-tight" data-testid={`example-brand-${ex.slug}`}>
          Cornerstone<span style={{ color: ex.accent }}>.</span>
        </div>
        <div className="flex items-center gap-3 text-[9px] font-bold text-[#2c2a29]/70 uppercase tracking-wider">
          {ex.navLinks.slice(0, 3).map((l) => <span key={l}>{l}</span>)}
        </div>
      </div>

      {/* Hero */}
      <div className="relative" style={{ height: 240 }}>
        <SafeImage
          src={ex.heroImage}
          alt={`${ex.name} hero`}
          className="w-full h-full"
          style={{ objectFit: "cover" }}
          testId={`example-hero-${ex.slug}`}
          fallbackLabel={ex.tag}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-4 text-white">
          <div className="text-[9px] font-bold tracking-[0.2em]" style={{ color: "#ffd4c2" }}>
            {ex.heroEyebrow}
          </div>
          <div className="font-display text-[19px] font-black leading-tight mt-1.5">
            {ex.heroHeadline}
          </div>
        </div>
      </div>

      {/* Sub-hero body: CTA row + peek of the "services" section */}
      <div className="p-4 space-y-3">
        <p className="text-[11px] text-[#5c5855] leading-relaxed line-clamp-2">{ex.heroSub}</p>
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-bold text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1"
            style={{ background: ex.accent }}
          >
            {ex.ctaPrimary} <ArrowRight className="w-3 h-3" />
          </span>
          <span className="text-[10px] font-bold text-[#2c2a29] px-3 py-1.5 rounded-full border border-[#2c2a29]/20">
            {ex.ctaSecondary}
          </span>
        </div>

        {/* Peek of the next section */}
        <div className="pt-3 border-t border-[#e6ded1]">
          <div className="text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: ex.accent }}>
            {ex.subSection.eyebrow}
          </div>
          <div className="font-display text-[13px] font-black text-[#2c2a29] leading-tight mt-1">
            {ex.subSection.title}
          </div>
          <div className="grid grid-cols-3 gap-1.5 mt-2">
            {ex.subSection.cards.map((c) => (
              <div
                key={c.title}
                className="rounded-md border border-[#e6ded1] bg-white p-1.5"
              >
                <div className="text-[8.5px] font-black text-[#2c2a29] leading-tight">{c.title}</div>
                <div className="text-[7.5px] text-[#5c5855] mt-0.5 line-clamp-2 leading-tight">{c.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/* 2. Restaurant template — Bella Roma Italian Kitchen                         */
/* -------------------------------------------------------------------------- */

function RestaurantPreview({ ex }) {
  return (
    <article
      className="shrink-0 w-[340px] sm:w-[380px] rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5"
      data-testid={`example-card-${ex.slug}`}
      style={{ background: ex.accentCream, color: ex.accent, height: 560 }}
    >
      {/* Nav */}
      <div className="h-11 flex items-center justify-between px-4 border-b border-black/10">
        <div className="font-serif italic text-[15px] tracking-tight" data-testid={`example-brand-${ex.slug}`} style={{ color: ex.accent }}>
          Bella Roma
        </div>
        <div className="flex items-center gap-3 text-[9px] font-bold uppercase tracking-wider opacity-70">
          {ex.navLinks.slice(0, 3).map((l) => <span key={l}>{l}</span>)}
        </div>
      </div>

      {/* Hero image */}
      <div className="relative" style={{ height: 240 }}>
        <SafeImage
          src={ex.heroImage}
          alt={`${ex.name} hero`}
          className="w-full h-full"
          style={{ objectFit: "cover" }}
          testId={`example-hero-${ex.slug}`}
          fallbackLabel={ex.tag}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-4 text-white">
          <div className="text-[9px] font-bold tracking-[0.22em]" style={{ color: ex.accentCream }}>
            {ex.heroEyebrow}
          </div>
          <div className="font-serif italic text-[19px] leading-tight mt-1.5" style={{ color: "#fff" }}>
            {ex.heroHeadline}
          </div>
        </div>
      </div>

      {/* Below-hero body */}
      <div className="p-4 space-y-3">
        <p className="text-[11px] leading-relaxed line-clamp-2" style={{ color: ex.accent, opacity: 0.85 }}>
          {ex.heroSub}
        </p>
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-bold text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1"
            style={{ background: ex.accent }}
          >
            {ex.ctaPrimary} <ArrowRight className="w-3 h-3" />
          </span>
          <span
            className="text-[10px] font-bold px-3 py-1.5 rounded-full border"
            style={{ color: ex.accent, borderColor: `${ex.accent}55` }}
          >
            {ex.ctaSecondary}
          </span>
        </div>

        {/* Peek of the "signature dishes" section */}
        <div className="pt-3 border-t border-black/10">
          <div className="text-[9px] font-bold uppercase tracking-[0.22em]" style={{ color: ex.accent, opacity: 0.85 }}>
            {ex.subSection.eyebrow}
          </div>
          <div className="font-serif italic text-[13px] leading-tight mt-1" style={{ color: ex.accent }}>
            {ex.subSection.title}
          </div>
          <div className="mt-2 divide-y divide-black/10">
            {ex.subSection.cards.map((c) => (
              <div key={c.title} className="py-1.5 flex items-baseline justify-between gap-2">
                <div>
                  <div className="text-[10.5px] font-bold" style={{ color: ex.accent }}>{c.title}</div>
                  <div className="text-[8.5px] mt-0.5 line-clamp-1" style={{ color: ex.accent, opacity: 0.75 }}>{c.body}</div>
                </div>
                <div className="text-[10px] italic" style={{ color: ex.accent, opacity: 0.6 }}>·  ·  ·</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/* 3. Ecommerce template — Crown & Stitch Hats                                 */
/* -------------------------------------------------------------------------- */

function EcommercePreview({ ex }) {
  return (
    <article
      className="shrink-0 w-[340px] sm:w-[380px] rounded-2xl overflow-hidden bg-black text-white shadow-xl ring-1 ring-white/5"
      data-testid={`example-card-${ex.slug}`}
      style={{ height: 560 }}
    >
      {/* Announcement bar */}
      <div
        className="h-6 flex items-center justify-center text-[9px] font-semibold uppercase tracking-[0.28em] text-white"
        style={{ background: ex.accent }}
      >
        Free shipping on U.S. orders $75+
      </div>

      {/* Nav */}
      <div className="h-11 flex items-center justify-between px-4 border-b border-white/10">
        <MenuIcon className="w-3.5 h-3.5 opacity-80" />
        <div className="font-display text-[15px] font-black tracking-[0.14em] uppercase" data-testid={`example-brand-${ex.slug}`}>
          Crown &amp; Stitch<span style={{ color: ex.accent }}>.</span>
        </div>
        <div className="flex items-center gap-2 opacity-80">
          <Search className="w-3.5 h-3.5" />
          <ShoppingBag className="w-3.5 h-3.5" />
        </div>
      </div>

      {/* Hero image */}
      <div className="relative" style={{ height: 240 }}>
        <SafeImage
          src={ex.heroImage}
          alt={`${ex.name} hero`}
          className="w-full h-full"
          style={{ objectFit: "cover" }}
          testId={`example-hero-${ex.slug}`}
          fallbackLabel={ex.tag}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/40" />
        <div className="absolute inset-x-0 bottom-0 p-4 text-white">
          <div className="inline-flex items-center gap-2">
            <span className="w-6 h-px bg-white" />
            <span className="text-[9px] tracking-[0.28em]" style={{ color: "#f5c9ae" }}>{ex.heroEyebrow}</span>
          </div>
          <div className="font-display text-[22px] font-black leading-[0.95] tracking-tighter mt-1.5">
            {ex.heroHeadline1}<br />
            <span className="italic font-normal">{ex.heroHeadline2}</span> {ex.heroHeadline3}
          </div>
        </div>
      </div>

      {/* Below-hero body */}
      <div className="p-4 space-y-3">
        <p className="text-[11px] text-neutral-300 leading-relaxed line-clamp-2">{ex.heroSub}</p>
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-semibold text-black uppercase tracking-[0.18em] px-3 py-1.5 bg-white inline-flex items-center gap-1.5"
          >
            {ex.ctaPrimary} <ArrowRight className="w-3 h-3" />
          </span>
          <span
            className="text-[10px] font-semibold text-white uppercase tracking-[0.18em] px-3 py-1.5 border border-white/60"
          >
            {ex.ctaSecondary}
          </span>
        </div>

        {/* Peek of the product grid */}
        <div className="pt-3 border-t border-white/10">
          <div className="flex items-center justify-between">
            <div className="text-[9px] font-bold uppercase tracking-[0.22em]" style={{ color: ex.accent }}>
              {ex.subSection.eyebrow}
            </div>
            <span className="text-[9px] text-white/60">Shop all →</span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {ex.subSection.products.map((p) => (
              <div key={p.title}>
                <div
                  className="rounded-none border border-white/10"
                  style={{
                    background: `linear-gradient(135deg, ${ex.accent}30 0%, #ffffff08 100%)`,
                    aspectRatio: "1 / 1",
                  }}
                />
                <div className="text-[8.5px] font-semibold mt-1 line-clamp-1">{p.title}</div>
                <div className="text-[8px] text-white/60">{p.price}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}
